import React, { useId } from "react";
import { cn } from "util/cn";

interface SectionShellProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
}

/**
 * Shared surface for community dashboard sections.
 * Keeps section framing consistent while allowing bespoke content layouts.
 */
const SectionShell: React.FC<SectionShellProps> = ({
  title,
  subtitle,
  actions,
  children,
  className,
  contentClassName,
}) => {
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        "overflow-hidden rounded-3xl border border-eoe-border bg-eoe-bg-card/90 shadow-sm backdrop-blur-sm dark:border-eoe-dark-border dark:bg-eoe-dark-bg-card/80",
        className
      )}
    >
      <div className="border-b border-eoe-border/80 px-5 py-4 dark:border-eoe-dark-border/80 md:px-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="space-y-1">
            <h2
              id={headingId}
              className="text-lg font-semibold tracking-tight text-eoe-text dark:text-eoe-dark-text md:text-xl"
            >
              {title}
            </h2>
            {subtitle ? (
              <p className="max-w-3xl text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {subtitle}
              </p>
            ) : null}
          </div>
          {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
      </div>
      <div className={cn("px-5 py-5 md:px-6", contentClassName)}>{children}</div>
    </section>
  );
};

export default SectionShell;

